import { useState } from "react";
import {
  FaEnvelope,
  FaExternalLinkAlt,
  FaMapMarkerAlt,
  FaPhone,
} from "react-icons/fa";
import {
  companyProjects,
  education,
  hobbyProjects,
  internshipsAndTraining,
  languages,
  personalInformation,
  skills,
  workExperience,
} from "../helper/constant";

export const About = () => (
  <div className="animate-fadeIn max-w-4xl">
    <h2 className="text-4xl font-bold text-white mb-8 tracking-tight">
      Summary
    </h2>

    <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-12">
      <div className="p-6 bg-neutral-900/50 border border-neutral-800 rounded-2xl hover:border-emerald-500/30 transition-all group">
        <div className="text-emerald-500 font-mono text-xs mb-2 uppercase tracking-tighter">
          Experience
        </div>
        <div className="text-white text-xl font-bold mb-1">
          3+ Years Professional
        </div>
        <p className="text-neutral-500 text-sm leading-relaxed">
          Proven track record in building scalable MERN stack systems and
          financial tools.
        </p>
      </div>

      <div className="p-6 bg-neutral-900/50 border border-neutral-800 rounded-2xl hover:border-emerald-500/30 transition-all group">
        <div className="text-emerald-500 font-mono text-xs mb-2 uppercase tracking-tighter">
          Education
        </div>
        <div className="text-white text-xl font-bold mb-1">MSc @ DTU</div>
        <p className="text-neutral-500 text-sm leading-relaxed">
          Specializing in AI and Algorithms at the Technical University of
          Denmark.
        </p>
      </div>

      <div className="p-6 bg-neutral-900/50 border border-neutral-800 rounded-2xl hover:border-emerald-500/30 transition-all group">
        <div className="text-emerald-500 font-mono text-xs mb-2 uppercase tracking-tighter">
          Focus
        </div>
        <div className="text-white text-xl font-bold mb-1">Intelligent Web</div>
        <p className="text-neutral-500 text-sm leading-relaxed">
          Bridging the gap between robust architecture and data-driven
          intelligence.
        </p>
      </div>
    </div>

    <div className="space-y-8 text-lg text-neutral-400 leading-relaxed font-light">
      <p>{personalInformation.summary}</p>
    </div>

    <div className="mt-16 flex flex-col md:flex-row md:items-center justify-between gap-6 border-t border-neutral-800 pt-8">
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2 text-emerald-500 font-mono text-xs uppercase tracking-[0.2em]">
          <span className="relative flex h-2 w-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75"></span>
            <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500"></span>
          </span>
          {personalInformation.location}
        </div>
        <div className="h-4 w-[1px] bg-neutral-800 hidden md:block"></div>
        <div className="text-neutral-500 font-mono text-xs uppercase tracking-[0.1em]">
          Available for Software Engineering Roles
        </div>
      </div>

      <div className="text-neutral-600 font-mono text-[10px] uppercase">
        Specializing in AI • Algorithms • Full-Stack
      </div>
    </div>
  </div>
);

const ProjectCard = ({ project }) => (
  <div className="group p-8 bg-neutral-900 border border-neutral-800 rounded-3xl hover:border-emerald-500/30 transition-all">
    <div className="flex justify-between items-center mb-4">
      <h3 className="text-2xl font-bold text-white group-hover:text-emerald-400 transition-colors">
        {project.title}
      </h3>
      <a
        href={project.url}
        target="_blank"
        rel="noopener noreferrer"
        className="text-neutral-600 hover:text-emerald-500 transition-colors"
      >
        <FaExternalLinkAlt size={20} />
      </a>
    </div>
    <p className="text-neutral-400 leading-relaxed text-sm italic">
      {project.desc}
    </p>
    <div className="mt-6">
      <a
        href={project.url}
        target="_blank"
        rel="noopener noreferrer"
        className="inline-flex items-center gap-2 text-[10px] uppercase tracking-widest text-emerald-500 font-mono hover:text-white transition-colors"
      >
        Open Project <FaExternalLinkAlt size={10} />
      </a>
    </div>
  </div>
);

export const Projects = () => (
  <div className="animate-fadeIn space-y-20">
    {" "}
    <section>
      <div className="flex items-center gap-4 mb-10">
        <h2 className="text-3xl font-bold text-white whitespace-nowrap">
          Personal Projects
        </h2>
        <div className="h-[1px] w-full bg-neutral-800" />
      </div>
      <div className="grid gap-8">
        {hobbyProjects.map((p, i) => (
          <ProjectCard key={i} project={p} />
        ))}
      </div>
    </section>
    <section>
      <div className="flex items-center gap-4 mb-10">
        <h2 className="text-3xl font-bold text-white whitespace-nowrap">
          Professional Projects
        </h2>
        <div className="h-[1px] w-full bg-neutral-800" />
      </div>
      <div className="grid gap-8">
        {companyProjects.map((p, i) => (
          <ProjectCard key={i} project={p} />
        ))}
      </div>
    </section>
  </div>
);

export const Skills = () => (
  <div className="animate-fadeIn space-y-12 pb-10">
    <h2 className="text-3xl font-bold text-white mb-8 tracking-tight">
      Skills
    </h2>

    <div className="grid gap-10">
      {Object.entries(skills).map(([category, items]) => (
        <div key={category} className="group">
          {/* Category Header */}
          <h4 className="text-[10px] uppercase tracking-[0.3em] text-neutral-500 mb-6 font-bold group-hover:text-emerald-500 transition-colors">
            {category.replace("_", " & ")}
          </h4>

          {/* Skill Pills */}
          <div className="flex flex-wrap gap-3">
            {items.map((skill) => (
              <div
                key={skill}
                className="px-5 py-2.5 bg-neutral-900 border border-neutral-800 rounded-xl text-sm text-neutral-400 hover:border-emerald-500/40 hover:text-white hover:bg-neutral-800/50 transition-all duration-300 cursor-default"
              >
                {skill}
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const Languages = () => (
  <div className="animate-fadeIn">
    <h2 className="text-3xl font-bold text-white mb-8">Languages</h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {languages.map((lang) => (
        <div
          key={lang.name}
          className="p-4 bg-neutral-900 border border-neutral-800 rounded-2xl hover:border-emerald-500/30 transition-all"
        >
          <div className="flex justify-between items-center mb-2">
            <span className="text-white font-medium">{lang.name}</span>
            <span className="text-xs font-mono text-emerald-500 uppercase tracking-widest">
              {lang.level}
            </span>
          </div>
          <div className="h-1 w-full bg-neutral-800 rounded-full overflow-hidden">
            <div
              className="h-full bg-emerald-500 rounded-full opacity-60"
              style={{ width: `${lang.strength}%` }}
            />
          </div>
        </div>
      ))}
    </div>
  </div>
);

const TechStack = ({ tech }) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const showExpand = tech.length > 5;
  const displayedTech = isExpanded ? tech : tech.slice(0, 5);

  return (
    <div className="flex flex-wrap gap-2 mt-4">
      {displayedTech.map((t, i) => (
        <span
          key={i}
          className="text-[10px] bg-black text-emerald-500 px-3 py-1 rounded-full font-mono border border-emerald-500/10"
        >
          {t}
        </span>
      ))}
      {showExpand && (
        <button
          onClick={() => setIsExpanded(!isExpanded)}
          className="text-[10px] text-neutral-500 hover:text-emerald-400 font-mono transition-colors ml-2 underline underline-offset-4"
        >
          {isExpanded ? "Show Less" : `+${tech.length - 5} More`}
        </button>
      )}
    </div>
  );
};

export const Experience = () => {
  const ExperienceItem = ({ exp }) => (
    <div className="relative pl-8 border-l border-neutral-800 group hover:border-emerald-500 transition-colors">
      <div className="absolute -left-[5px] top-0 w-[9px] h-[9px] bg-neutral-900 border border-neutral-700 rounded-full group-hover:bg-emerald-500 transition-colors" />
      <div className="flex flex-col md:flex-row md:justify-between md:items-center mb-4 gap-2">
        <div>
          <h3 className="text-xl font-bold text-gray-200 tracking-tight">
            {exp.role}
          </h3>
          <p className="text-emerald-500 font-medium">{exp.company}</p>
        </div>
        <span className="text-xs font-mono text-neutral-500 bg-neutral-900 px-3 py-1 rounded-full border border-neutral-800 whitespace-nowrap">
          {exp.date}
        </span>
      </div>
      <p className="text-neutral-400 leading-relaxed text-sm max-w-3xl">
        {exp.desc}
      </p>
      <TechStack tech={exp.tech} />
    </div>
  );

  return (
    <div className="animate-fadeIn space-y-16">
      <section>
        <h2 className="text-3xl font-bold text-white mb-10">Work Experience</h2>
        <div className="space-y-12">
          {workExperience.map((exp, i) => (
            <ExperienceItem key={i} exp={exp} />
          ))}
        </div>
      </section>

      <section>
        <h2 className="text-3xl font-bold text-white mb-10">
          Internships & Training
        </h2>
        <div className="space-y-12">
          {internshipsAndTraining.map((exp, i) => (
            <ExperienceItem key={i} exp={exp} />
          ))}
        </div>
      </section>
    </div>
  );
};
export const Education = () => (
  <div className="animate-fadeIn space-y-12">
    <h2 className="text-3xl font-bold text-white mb-8">Academic Foundation</h2>
    <div className="grid gap-8">
      {education.map((edu, i) => (
        <div
          key={i}
          className="p-8 bg-neutral-900 border border-neutral-800 rounded-3xl hover:border-emerald-500/20 transition-all"
        >
          <div className="flex justify-between items-start mb-4">
            <div>
              <h3 className="text-xl font-bold text-white">{edu.school}</h3>
              <p className="text-emerald-500 font-medium mt-1">{edu.degree}</p>
            </div>
            <span className="text-xs font-mono text-neutral-500">
              {edu.date}
            </span>
          </div>
          <div className="flex items-center gap-4 text-xs text-neutral-500 uppercase tracking-widest">
            <span>{edu.loc}</span>
            {edu.details && (
              <>
                <span className="h-1 w-1 bg-neutral-700 rounded-full" />
                <span className="text-neutral-400">{edu.details}</span>
              </>
            )}
          </div>
        </div>
      ))}
    </div>
  </div>
);

export const Links = () => (
  <div className="animate-fadeIn">
    <h2 className="text-3xl font-bold text-white mb-8">
      Connect & Collaborate
    </h2>
    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
      {/* Contact Card */}
      <div className="p-8 bg-neutral-900 border border-neutral-800 rounded-3xl space-y-4">
        <h4 className="text-xs uppercase tracking-[0.2em] text-neutral-500 mb-6">
          Direct Contact
        </h4>
        <a
          href={`mailto:${personalInformation.email}`}
          className="flex items-center gap-4 text-neutral-300 hover:text-emerald-500 transition-colors"
        >
          <div className="p-3 bg-black rounded-xl">
            <FaEnvelope />
          </div>
          <span className="text-sm truncate">{personalInformation.email}</span>
        </a>
        <div className="flex items-center gap-4 text-neutral-300 hover:text-emerald-500 transition-colors">
          <div className="p-3 bg-black rounded-xl">
            <FaPhone />
          </div>
          <span className="text-sm text-white">
            {personalInformation.phone}
          </span>
        </div>
      </div>

      {/* Social Links */}
      <div className="grid grid-cols-2 gap-4">
        {personalInformation.links.map((l, i) => (
          <a
            key={i}
            href={l.url}
            target="_blank"
            rel="noopener noreferrer"
            className="flex flex-col items-center justify-center p-6 bg-neutral-900 border border-neutral-800 rounded-3xl hover:bg-emerald-500 hover:text-black transition-all group"
          >
            <l.icon
              size={28}
              className="mb-3 group-hover:scale-110 transition-transform"
            />
            <span className="text-[10px] font-bold uppercase tracking-widest">
              {l.label}
            </span>
          </a>
        ))}
      </div>
    </div>
  </div>
);
